import { useStyles } from "./MyIssues.styles";
import NoData from "components/NoData/NoData.view";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { Card, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useMemo, useState } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import useMyIssuesColumns from "./useMyIssuesColumns";
import { useNavigate } from "react-router-dom";
import { IssueEntity } from "entities/RCA/IssueEntity/IssueEntity";
import { MainTable } from "components/MainTable";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import MainCardButtonAction from "components/MainCardButtonAction";
import useRolesPermissions from "hooks/useRolesPermissions";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { IMyIssuesFilters } from "./MyIssuesInterfaces";
import RCACreateEditIssuesModal from "pages/Corporate/RCA/components/RCACreateEditIssuesModal";

const MyIssues = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const { user_id } = useContext(UserContext);
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();
    const tableColumns = useMyIssuesColumns() as unknown as IMainTableColumn[];
    const [filters, setFilters] = useState<IMyIssuesFilters>({ showCompleted: false });
    const { RCA_PERMISSIONS } = useRolesPermissions();
    const [showCreateIssueModal, setShowCreateIssueModal] = useState(false);
    const navigate = useNavigate();

    const queryTableOperationFilters = useMemo(
        () => ({
            order,
            orderBy,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        }),
        [order, orderBy, rowsPerPage, page],
    );
    const { loading, userIssues, apolloQueryError, refetch, totalCount } = IssueEntity.GetUserIssues(
        queryTableOperationFilters,
        user_id!,
        filters,
    );
    return (
        <Card className={mainClasses.contentCard}>
            <div className={mainClasses.contentCardHeader}>
                <div className={classes.titleContainer}>
                    <span className={mainClasses.contentCardTitleCapitalized}>{t("rca.rca_dashboard.my_issues")}</span>
                    <MainCardButtonAction
                        disabled={!RCA_PERMISSIONS?.dashboard.canCreateIssues}
                        title={t("rca.rca_dashboard.add_issue")}
                        renderIcon={() => <AddCircleIcon />}
                        onClick={() => setShowCreateIssueModal(true)}
                    />
                </div>
                <div className={mainClasses.contentCardActions}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value={true}
                                    color="info"
                                    onChange={(_, checked) => {
                                        setFilters({ ...filters, showCompleted: checked });
                                    }}
                                />
                            }
                            label={t("rca.rca_dashboard.show_completed")}
                        />
                    </FormGroup>
                </div>
            </div>

            <div className={mainClasses.contentCardContent}>
                {userIssues && !userIssues.length && (
                    <NoData className={mainClasses.contentNoData} title={t("data.no_data_short")} />
                )}
                {!!userIssues && !!userIssues.length && (
                    <MainTable
                        maxHeight={false}
                        customStyle={`${mainClasses.contentTable} ${classes.mainTable}`}
                        paperElevation={0}
                        paperCustomStyle={mainClasses.contentTablePaperMyDashboard}
                        columns={tableColumns}
                        tableCellCustomStyle={mainClasses.contentTableCell}
                        rows={!loading && !apolloQueryError ? userIssues : []}
                        loading={loading}
                        tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                        handleRequestSortParent={handleRequestSort}
                        handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                        handleChangePageParent={handleChangePage}
                        pagination
                        count={totalCount}
                        customStyleTypography={{ width: "100%" }}
                        querySorted={!!orderBy}
                        onRowClick={(row: { issue_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}/${row.issue_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`,
                                    },
                                },
                            );
                        }}
                    />
                )}
            </div>
            <RCACreateEditIssuesModal
                open={showCreateIssueModal}
                onClose={() => setShowCreateIssueModal(false)}
                onAddIssue={async () => {
                    await refetch();
                }}
                onAddIssueRedirectTo={`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`}
            />
        </Card>
    );
};

export default MyIssues;
